/**
 * Hooks React Query pour les exercices V2.
 *
 * Lecture : edge fn `exercise-get-for-edit` (payload normalisé).
 * Écriture : edge fn `exercise-blocks-mutate` (batch d'ops atomiques).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import type { BlockMutationOp, ExerciseV2Payload } from './types';

export const exerciseV2QueryKey = (exerciseId: string) => ['exercise-v2', exerciseId] as const;

export interface CreateV2ExerciseInput {
  chapter_id: string;
  lesson_id?: string | null;
  /** Énoncé brut collé/importé — sert de `statement_md` legacy (NOT NULL). */
  raw_statement: string;
  /** Titre provisoire ; affiné ensuite par la phase cohérence / l'éditeur. */
  title?: string;
}

/**
 * Crée un exercice NATIVEMENT en V2 (`content_version='v2'`, brouillon, sans
 * étapes ni choix V1). Les blocs sont ensuite produits par les phases de
 * génération. C'est le seul chemin de création V2 natif (l'autre voie étant la
 * migration V1→V2). `statement_md` et `title` sont NOT NULL côté table : on les
 * amorce, l'éditeur V2 les affinera.
 */
export function useCreateV2Exercise() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({
      chapter_id,
      lesson_id,
      raw_statement,
      title,
    }: CreateV2ExerciseInput): Promise<string> => {
      const { data, error } = await supabase
        .from('exercises')
        .insert({
          chapter_id,
          lesson_id: lesson_id ?? null,
          created_by: user?.id ?? null,
          title: (title?.trim() || 'Nouvel exercice').slice(0, 200),
          statement_md: raw_statement.trim().slice(0, 16384) || '—',
          difficulty: 'medium',
          estimated_minutes: 5,
          visual_blocks: [],
          content_version: 'v2',
          status: 'draft',
        })
        .select('id')
        .single();
      if (error || !data) throw new Error(error?.message ?? 'Échec création exercice V2');
      return data.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
      qc.invalidateQueries({ queryKey: ['admin-exercises'] });
    },
  });
}

export function useExerciseV2(exerciseId: string | null | undefined) {
  return useQuery({
    queryKey: exerciseV2QueryKey(exerciseId ?? ''),
    enabled: !!exerciseId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ExerciseV2Payload> => {
      const { data, error } = await supabase.functions.invoke<ExerciseV2Payload>(
        'exercise-get-for-edit',
        { body: { exercise_id: exerciseId } },
      );
      if (error) throw error;
      if (!data) throw new Error('empty_response');
      return data;
    },
  });
}

export interface BlocksMutateResponse {
  ok: boolean;
  applied: Array<{ op: string; id?: string; client_id?: string }>;
  client_id_map: Record<string, string>;
}

export function useBlocksMutate(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ops: BlockMutationOp[]): Promise<BlocksMutateResponse> => {
      const { data, error } = await supabase.functions.invoke<BlocksMutateResponse>(
        'exercise-blocks-mutate',
        { body: { exercise_id: exerciseId, ops } },
      );
      if (error) {
        // supabase-js FunctionsHttpError : le body utile (avec le reason) est
        // dans error.context (Response). On l'extrait avant de jeter.
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.text === 'function') {
          try {
            const text = await ctx.text();
            throw new Error(`${(error as Error).message} — ${text}`);
          } catch (parseErr) {
            // Si parseErr est notre Error fraîchement créée, on la propage telle quelle.
            if (parseErr instanceof Error && parseErr.message.includes('—')) throw parseErr;
            throw error;
          }
        }
        throw error;
      }
      if (!data) throw new Error('empty_response');
      return data;
    },
    // onSettled : invalide même en erreur. L'edge fn est désormais atomique
    // (tout ou rien), mais on relit quand même pour rester cohérent avec un
    // éventuel échec partiel hérité et avec les hooks de génération.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) });
    },
  });
}

/**
 * Helper : génère un client_id stable et unique pour une nouvelle op insert.
 * Utilisé pour résoudre les parent_id transitifs dans un batch.
 */
let clientIdCounter = 0;
export function nextClientId(prefix = 'tmp'): string {
  clientIdCounter += 1;
  return `${prefix}_${Date.now()}_${clientIdCounter}`;
}
